/* RESPONSIBLE TEAM: team-channels */
import { modifier } from 'ember-modifier';

function isElementVisible(element: HTMLElement): boolean {
  if (!element || !document.contains(element)) {
    return false;
  }
  let style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

export interface ClickOutsideSignature {
  Element: HTMLElement;
  Args: {
    Named: {
      ignoredSelectors?: string | string[];
    };
    Positional: [(event: MouseEvent) => void];
  };
}

/**
 * Checks if the element or any of its parents match the given selector
 */
function hasParentMatchingSelector(element: Element | null, selector: string): boolean {
  if (!element) {
    return false;
  }

  if (element.matches(selector)) {
    return true;
  }

  return hasParentMatchingSelector(element.parentElement, selector);
}

const ClickOutsideModifier = modifier<ClickOutsideSignature>(
  (element, [callback], { ignoredSelectors }) => {
    if (!callback) {
      return;
    }

    function handleClick(event: MouseEvent) {
      let target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      // Check if the click is outside the element
      if (!element.contains(target) && isElementVisible(element)) {
        // If we have ignored selectors, check if the clicked element matches any of them
        if (ignoredSelectors) {
          let selectors = Array.isArray(ignoredSelectors) ? ignoredSelectors : [ignoredSelectors];

          // If the clicked element or any of its parents match any of the ignored selectors, don't execute the callback
          let shouldIgnore = selectors.some((selector) =>
            hasParentMatchingSelector(target, selector),
          );

          if (shouldIgnore) {
            return;
          }
        }

        callback(event);
      }
    }

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  },
  { eager: false },
);

export default ClickOutsideModifier;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'click-outside': typeof ClickOutsideModifier;
  }
}
